import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import './PaymentForm.css';

function PaymentForm({ domain, onSuccess, onCancel }) {
  const [pricing, setPricing] = useState(null);
  const [currency, setCurrency] = useState('TRY');
  const [months, setMonths] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [step, setStep] = useState(0); // 0: loading/billing check, 1: plan seçimi, 2: kart bilgileri

  // 🆕 Billing validation
  const [billingValid, setBillingValid] = useState(false);
  const [checkingBilling, setCheckingBilling] = useState(true);

  // PayTR form data (backend'den gelecek)
  const [formData, setFormData] = useState(null);
  const [formAction, setFormAction] = useState('');
  const paytrFormRef = useRef(null);

  // Kart bilgileri (sadece frontend'de tutulur, backend'e GİTMEZ)
  const [cardData, setCardData] = useState({
    cc_owner: '',
    card_number: '',
    expiry_month: '',
    expiry_year: '',
    cvv: ''
  });


  // Free trial
  const [freeTrialEligibility, setFreeTrialEligibility] = useState(null);
  const [checkingEligibility, setCheckingEligibility] = useState(false);
  const [activatingTrial, setActivatingTrial] = useState(false);

  // Domain doğrulama kaldırıldı (state'ler temizlendi)

  const { t, i18n } = useTranslation();
  const lang = i18n.language || 'en';

  // rootDomain memo kaldırıldı (doğrulama ile birlikte)

  useEffect(() => {
    // Domain doğrulama kaldırıldı — direkt billing check'e geç
    checkBillingProfile();
  }, [domain]);

  // 🆕 ═══════════════════════════════════════════════════════════════
  // BILLING PROFILE CHECK
  // ═══════════════════════════════════════════════════════════════
  const checkBillingProfile = async () => {
    setCheckingBilling(true);
    try {
      const response = await axios.get('/billing/validate');
      if (response.data.is_valid) {
        setBillingValid(true);
        await fetchPricingAndEligibility();
      } else {
        setBillingValid(false);
        setError(lang === 'tr' 
          ? 'Lütfen önce fatura bilgilerinizi doldurun' 
          : 'Please complete your billing information first');
        setStep(0);
      }
    } catch (error) {
      console.error('Billing validation error:', error);
      setBillingValid(false);
      setError(lang === 'tr' 
        ? 'Fatura bilgileriniz kontrol edilemedi' 
        : 'Could not validate billing information');
      setStep(0);
    } finally {
      setCheckingBilling(false);
    }
  };

  const fetchPricingAndEligibility = async () => {
    try {
      const pricingRes = await axios.get('/payment/pricing');
      setPricing(pricingRes.data.pricing);
      setMonths(pricingRes.data.pricing.min_months || 1);
      await checkFreeTrialEligibility();
      setStep(1); // 🆕 Billing valid, plan seçimine geç
    } catch (err) {
      setError(t('error.loadingData') || 'Veriler yüklenemedi');
    }
  };

  const checkFreeTrialEligibility = async () => {
    setCheckingEligibility(true);
    try {
      const response = await axios.post('/subscription/check-free-trial-eligibility', {
        domainId: domain.id
      });
      setFreeTrialEligibility(response.data);
    } catch (err) {
      setFreeTrialEligibility({ eligible: false, reason_code: 'CHECK_FAILED' });
    } finally {
      setCheckingEligibility(false);
    }
  };

  // copyToClipboard ve handleVerifyDomain kaldırıldı (doğrulama ile birlikte)

  // ═══════════════════════════════════════════════════════════════
  // FİYAT HESAPLAMA
  // ═══════════════════════════════════════════════════════════════
  const calculation = useMemo(() => {
    if (!pricing) return null;
    const monthlyUsd = pricing.monthly_price_usd;
    const tryMultiplier = pricing.currency_multiplier_try;
    const totalUsd = monthlyUsd * months;
    const totalTry = totalUsd * tryMultiplier;
    const monthlyTry = monthlyUsd * tryMultiplier;

    return {
      monthlyUsd,
      monthlyTry: Math.round(monthlyTry * 100) / 100,
      totalUsd: Math.round(totalUsd * 100) / 100,
      totalTry: Math.round(totalTry * 100) / 100,
      tryMultiplier,
      amount: currency === 'USD' ? Math.round(totalUsd * 100) / 100 : Math.round(totalTry * 100) / 100,
      currencySymbol: currency === 'USD' ? '$' : '₺'
    };
  }, [pricing, months, currency]);

  const monthOptions = useMemo(() => {
    if (!pricing) return [];
    const opts = [];
    for (let i = pricing.min_months; i <= pricing.max_months; i++) opts.push(i);
    return opts;
  }, [pricing]);

  // ═══════════════════════════════════════════════════════════════
  // FREE TRIAL
  // ═══════════════════════════════════════════════════════════════
  const handleFreeTrialActivation = async () => {
    setActivatingTrial(true);
    setError('');
    try {
      const response = await axios.post('/payment/confirm', {
        paymentIntentId: `free_trial_${Date.now()}`,
        domain: domain.domain_url,
      });
      if (onSuccess) onSuccess(response.data.subscription);
    } catch (err) {
      const errorCode = err.response?.data?.error_code;
      if (errorCode === 'DOMAIN_TRIAL_ALREADY_USED') {
        setError(t('error.domainTrialUsed') || 'Bu domain zaten ücretsiz deneme kullanmış');
      } else if (errorCode === 'USER_TRIAL_LIMIT_EXCEEDED') {
        setError(t('error.userTrialLimitExceeded') || 'Ücretsiz deneme hakkınız dolmuş');
      } else {
        setError(err.response?.data?.error || t('error.paymentFailed') || 'İşlem başarısız');
      }
    } finally {
      setActivatingTrial(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // ADIM 2'YE GEÇ - Backend'den token al
  // ═══════════════════════════════════════════════════════════════
  const handleProceedToPayment = async () => {
    // ✅ Artık burada token almıyoruz
    // Sadece step 2'ye geçiyoruz
    // Token, form submit edildiğinde alınacak
    setStep(2);
  };

  // ═══════════════════════════════════════════════════════════════
  // KART BİLGİSİ GİRİŞİ
  // ═══════════════════════════════════════════════════════════════
  const handleCardInputChange = (e) => {
    const { name, value } = e.target;
    if (name === 'card_number') {
      const cleaned = value.replace(/\D/g, '');
      const formatted = cleaned.replace(/(\d{4})/g, '$1 ').trim();
      setCardData(prev => ({ ...prev, [name]: formatted.slice(0, 19) }));
    } else if (name === 'cvv') {
      setCardData(prev => ({ ...prev, [name]: value.replace(/\D/g, '').slice(0, 4) }));
    } else if (name === 'expiry_month') {
      const m = parseInt(value);
      if (value === '' || (m >= 0 && m <= 12)) {
        setCardData(prev => ({ ...prev, [name]: value.slice(0, 2) }));
      }
    } else if (name === 'expiry_year') {
      setCardData(prev => ({ ...prev, [name]: value.replace(/\D/g, '').slice(0, 2) }));
    } else {
      setCardData(prev => ({ ...prev, [name]: value }));
    }
  };


  const validateCard = () => {

    // Kart bilgileri doğrulama
    const { cc_owner, card_number, expiry_month, expiry_year, cvv } = cardData;
    if (!cc_owner || cc_owner.trim().length < 3) {
      setError(t('error.enterCardName') || 'Kart üzerindeki ismi girin');
      return false;
    }
    if (!card_number || card_number.replace(/\s/g, '').length < 13) {
      setError(t('error.invalidCardNumber') || 'Geçersiz kart numarası');
      return false;
    }
    const month = parseInt(expiry_month);
    if (!month || month < 1 || month > 12) {
      setError(t('error.validMonth') || 'Geçerli bir ay girin');
      return false;
    }
    const year = parseInt(expiry_year);
    const currentYear = new Date().getFullYear() % 100;
    if (!year || year < currentYear) {
      setError(t('error.validYear') || 'Geçerli bir yıl girin');
      return false;
    }
    if (!cvv || cvv.length < 3) {
      setError(t('error.validCVV') || 'Geçerli CVV girin');
      return false;
    }
    return true;
  };

  // ═══════════════════════════════════════════════════════════════
  // FORM SUBMIT → PayTR'ye POST
  // ═══════════════════════════════════════════════════════════════
  const handlePaytrSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!validateCard()) return;

    // ✅ ÖNCE token al
    setLoading(true);

    try {
      const res = await axios.post('/payment/create-token', {
        domainId: domain.id,
        months,
        currency
      });

      if (res.data.test_mode) {
        alert(
          `Test Modu\n\n` +
          `Domain: ${domain.domain_url}\n` +
          `Süre: ${months} ay\n` +
          `Tutar: ${calculation.currencySymbol}${calculation.amount.toFixed(2)}\n` +
          `Merchant OID: ${res.data.merchant_oid}\n\n` +
          `PayTR .env ayarları yapılınca gerçek ödeme alınacaktır.`
        );
        if (onSuccess) onSuccess(res.data);
        return;
      }

      // Token alındı, şimdi formu PayTR'ye submit et
      setFormData(res.data.form_data);
      setFormAction(res.data.form_action);

      // Form verilerini DOM'a ekle ve submit et
      setTimeout(() => {
        if (paytrFormRef.current) {
          const cardInput = paytrFormRef.current.querySelector('input[name="card_number"]');
          if (cardInput) {
            cardInput.value = cardInput.value.replace(/\s/g, '');
          }
          paytrFormRef.current.submit();
        }
      }, 100);

    } catch (err) {
      const msg = err.response?.data?.message || t('error.paymentFailed') || 'Ödeme başlatılamadı';
      setError(msg);
      setLoading(false);
    }
  };

  // ═══════════════════════════════════════════════════════════════
  // RENDER: BILLING NOT COMPLETE
  // ═══════════════════════════════════════════════════════════════

  // ═══════════════════════════════════════════════════════════════
  // DOĞRULAMA EKRANI KALDIRILDI — domain doğrulama artık yok
  // ═══════════════════════════════════════════════════════════════

  if (!billingValid && !checkingBilling) {
    return (
      <div className="payment-form-container">
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontSize: '64px', marginBottom: '16px' }}>⚠️</div>
          <h2 style={{ marginBottom: '16px' }}>{lang === 'tr' ? 'Fatura Bilgileri Gerekli' : 'Billing Information Required'}</h2>
          <p style={{ marginBottom: '24px', color: '#666' }}>
            {lang === 'tr' 
              ? 'Ödeme yapabilmek için önce fatura bilgilerinizi doldurmanız gerekmektedir.' 
              : 'You need to complete your billing information before making a payment.'}
          </p>
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
            <button onClick={onCancel} className="btn-secondary">
              {t('action.cancel') || 'İptal'}
            </button>
            <button 
              onClick={() => window.location.href = '/billing'} 
              className="btn-primary"
            >
              {lang === 'tr' ? 'Fatura Bilgilerimi Tamamla' : 'Complete Billing Information'}
            </button>
          </div>
        </div>
      </div>
    );
  }
  // ═══════════════════════════════════════════════════════════════
  // RENDER: LOADING / BILLING CHECK
  // ═══════════════════════════════════════════════════════════════
  if (checkingBilling || step === 0) {
    return (
      <div className="payment-form-container">
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          {checkingBilling ? (
            <>
              <div className="spinner" style={{ margin: '0 auto 16px' }}></div>
              <p>{lang === 'tr' ? 'Yükleniyor...' : 'Loading...'}</p>
            </>
          ) : !billingValid ? (
            <>
              <div style={{ fontSize: '3rem', marginBottom: '16px' }}>⚠️</div>
              <h3>{lang === 'tr' ? 'Fatura Bilgisi Gerekli' : 'Billing Information Required'}</h3>
              <p style={{ color: '#6b7280', marginBottom: '20px' }}>
                {error || (lang === 'tr' ? 'Lütfen önce fatura bilgilerinizi doldurun' : 'Please complete your billing information first')}
              </p>
              <button 
                className="btn-primary" 
                onClick={() => window.location.href = '/settings?tab=billing'}
                style={{ marginBottom: '10px' }}
              >
                {lang === 'tr' ? 'Fatura Bilgilerini Doldur' : 'Complete Billing Information'}
              </button>
              <button 
                className="btn-secondary" 
                onClick={onCancel}
              >
                {lang === 'tr' ? 'İptal' : 'Cancel'}
              </button>
            </>
          ) : (
            <>
              <div className="spinner" style={{ margin: '0 auto 16px' }}></div>
              <p>{lang === 'tr' ? 'Yükleniyor...' : 'Loading...'}</p>
            </>
          )}
        </div>
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // ADIM 2 - KART BİLGİSİ FORMU (PayTR'ye POST olur)
  // ═══════════════════════════════════════════════════════════════
  if (step === 2) {
    return (
      <>
        <div className="modal-header" style={{paddingBottom: '0px'}}>
          <button type="button" className="back-button" onClick={() => { setStep(1); setFormData(null); }}>
            ← {t('payment.selectPlan') || 'Back'}
          </button>
          <button className="modal-close payment" onClick={onCancel}>×</button>
        </div>
        <div className="payment-form-container">
          <div className="payment-form-header">
            <h2>{t('payment.paymentInfo') || 'Payment Info'}</h2>
            <p className="domain-info">
              <strong>{domain.domain_url}</strong> — {months} {t('payment.months') || 'month'} — {calculation?.currencySymbol}{calculation?.amount.toFixed(2)}
            </p>
          </div>

          {error && (
            <div className="payment-error"><span>⚠️</span> {error}</div>
          )}

          <form
            ref={paytrFormRef}
            action={formAction || '#'}
            method="POST"
            onSubmit={handlePaytrSubmit}
            className="payment-form"
          >
            {/* Hidden inputs - backend'den gelen PayTR verileri */}
            {formData && Object.entries(formData).map(([key, value]) => (
              <input type="hidden" name={key} value={value} key={key} />
            ))}

            {/* Kart Bilgileri */}
            <div className="form-section-title card_info_head" style={{ marginTop: '20px', display: 'none' }}>
              💳 {t('payment.cardInfo') || 'Kart Bilgileri'}
            </div>
            <div className="form-group">
              <label htmlFor="cc_owner">{t('payment.cardName') || 'Kart Üzerindeki İsim'}</label>
              <input
                type="text"
                id="cc_owner"
                name="cc_owner"
                value={cardData.cc_owner}
                onChange={handleCardInputChange}
                placeholder={t('payment.fullNamePlaceholder') || 'Name Surname'}
                required
                autoComplete="cc-name"
              />
            </div>

            <div className="form-group">
              <label htmlFor="card_number">{t('payment.cardNumber') || 'Kart Numarası'}</label>
              <input
                type="text"
                id="card_number"
                name="card_number"
                value={cardData.card_number}
                onChange={handleCardInputChange}
                placeholder="1234 5678 9012 3456"
                required
                autoComplete="cc-number"
                inputMode="numeric"
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="expiry_month">{t('payment.expiryMonth') || 'Ay'}</label>
                <input
                  type="text"
                  id="expiry_month"
                  name="expiry_month"
                  value={cardData.expiry_month}
                  onChange={handleCardInputChange}
                  placeholder="MM"
                  required
                  autoComplete="cc-exp-month"
                  inputMode="numeric"
                  maxLength="2"
                />
              </div>

              <div className="form-group">
                <label htmlFor="expiry_year">{t('payment.expiryYear') || 'Yıl'}</label>
                <input
                  type="text"
                  id="expiry_year"
                  name="expiry_year"
                  value={cardData.expiry_year}
                  onChange={handleCardInputChange}
                  placeholder="YY"
                  required
                  autoComplete="cc-exp-year"
                  inputMode="numeric"
                  maxLength="2"
                />
              </div>

              <div className="form-group">
                <label htmlFor="cvv">{t('payment.cvv') || 'CVV'}</label>
                <input
                  type="text"
                  id="cvv"
                  name="cvv"
                  value={cardData.cvv}
                  onChange={handleCardInputChange}
                  placeholder="123"
                  required
                  autoComplete="cc-csc"
                  inputMode="numeric"
                  maxLength="4"
                />
              </div>
            </div>

            {/* Ödeme Özeti */}
            <div className="plan-summary">
              <div className="summary-row">
                <span>{t('payment.monthlyPrice') || 'Aylık Fiyat'}:</span>
                <span>
                  {currency === 'USD' ? `$${calculation.monthlyUsd.toFixed(2)}` : `₺${calculation.monthlyTry.toFixed(2)}`}
                </span>
              </div>
              <div className="summary-row">
                <span>{t('payment.duration') || 'Süre'}:</span>
                <span>{months} {t('payment.months') || 'Ay'}</span>
              </div>
              {currency === 'TRY' && (
                <div className="summary-row" style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                  <span>{t('payment.exchangeRate') || 'Döviz Kuru'}:</span>
                  <span>1 USD = ₺{calculation?.tryMultiplier}</span>
                </div>
              )}
              <div className="summary-row total">
                <span>{t('payment.amountDue') || 'Ödenecek Tutar'}:</span>
                <strong>{calculation?.currencySymbol}{calculation?.amount.toFixed(2)}</strong>
              </div>
            </div>

            <div className="payment-actions">
              <button type="button" className="btn-secondary" onClick={onCancel} style={{border:'1px solid #f0f0f0', borderRadius: '4px'}}>
                {t('action.cancel') || 'İptal'}
              </button>
              <button type="submit" className="btn-primary btn-pay">
                {calculation?.currencySymbol}{calculation?.amount.toFixed(2)} {t('payment.pay') || 'Öde'}
              </button>
            </div>

            <p style={{ marginTop: '12px', fontSize: '0.75rem', color: '#9ca3af', textAlign: 'center' }}>
              🔒 {t('payment.securityNote') || 'Kart bilgileriniz güvenli ödeme altyapısı PayTR üzerinden işlenmektedir.'}
            </p>
          </form>
        </div>
      </>
    );
  }

  // ═══════════════════════════════════════════════════════════════
  // ADIM 1 - PARA BİRİMİ + AY SEÇİMİ
  // ═══════════════════════════════════════════════════════════════
  return (
    <div className="payment-form-container">
      <div className="payment-form-header">
        <h2>{t('payment.title') || 'Ödeme'}</h2>
        <p className="domain-info">
          <strong>{domain.domain_url}</strong> {t('payment.forDomain') || 'için ödeme'}
        </p>
      </div>

      {error && (
        <div className="payment-error"><span>⚠️</span> {error}</div>
      )}

      {/* ÜCRETSİZ DENEME */}
      {freeTrialEligibility && freeTrialEligibility.eligible && pricing && (
        <div className="free-trial-section">
          <div className="free-trial-card">
            <div className="free-trial-badge">🎁 {t('payment.free') || 'Ücretsiz'}</div>
            <h4>{pricing.free_trial_days} {t('payment.freeTrial') || 'Günlük Ücretsiz Deneme'}</h4>
            <p>{t('payment.freeTrialDesc') || 'Kredi kartı gerekmeden hemen başlayın'}</p>
            <button
              type="button"
              className="btn-trial"
              onClick={handleFreeTrialActivation}
              disabled={activatingTrial}
            >
              {activatingTrial
                ? (t('action.processing') || 'İşleniyor...')
                : (t('payment.startFreeTrial') || 'Ücretsiz Denemeyi Başlat')}
            </button>
          </div>
          <div className="or-divider"><span>{t('payment.or') || 'veya'}</span></div>
        </div>
      )}

      {!pricing ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          {t('loading.text') || 'Yükleniyor...'}
        </div>
      ) : (
        <div className="payment-step">
          {/* PARA BİRİMİ SEÇİMİ */}
          <div className="form-section">
            <label className="section-label">{t('payment.selectCurrency') || 'Para Birimi'}</label>
            <div className="currency-selector">
              <button
                type="button"
                className={`currency-btn ${currency === 'TRY' ? 'selected' : ''}`}
                onClick={() => setCurrency('TRY')}
              >
                🇹🇷 Türk Lirası (₺)
              </button>
              <button
                type="button"
                className={`currency-btn ${currency === 'USD' ? 'selected' : ''}`}
                onClick={() => setCurrency('USD')}
              >
                🇺🇸 US Dollar ($)
              </button>
            </div>
          </div>

          {/* AY SEÇİMİ */}
          <div className="form-section">
            <label className="section-label">{t('payment.selectDuration') || 'Süre Seçimi'}</label>
            <select
              value={months}
              onChange={(e) => setMonths(Number(e.target.value))}
              className="months-select"
            >
              {monthOptions.map(m => {
                const usdTotal = pricing.monthly_price_usd * m;
                const tryTotal = usdTotal * pricing.currency_multiplier_try;
                const displayAmount = currency === 'USD' ? `$${usdTotal.toFixed(2)}` : `₺${tryTotal.toFixed(2)}`;
                return (
                  <option key={m} value={m}>
                    {m} {t('payment.months') || 'Ay'} — {displayAmount}
                  </option>
                );
              })}
            </select>

            {/* Hızlı seçim */}
            <div className="quick-months">
              {[1, 3, 6, 12].filter(m => m >= pricing.min_months && m <= pricing.max_months).map(m => (
                <button
                  key={m}
                  type="button"
                  className={`quick-month-btn ${months === m ? 'selected' : ''}`}
                  onClick={() => setMonths(m)}
                >
                  {m} Ay
                </button>
              ))}
            </div>
          </div>

          {/* FİYAT ÖZETİ */}
          {calculation && (
            <div className="plan-summary">
              <div className="summary-row">
                <span>{t('payment.monthlyPrice') || 'Aylık Fiyat'}:</span>
                <strong>
                  {currency === 'USD' ? `$${calculation.monthlyUsd.toFixed(2)}` : `₺${calculation.monthlyTry.toFixed(2)}`}
                </strong>
              </div>
              <div className="summary-row">
                <span>{t('payment.duration') || 'Süre'}:</span>
                <strong>{months} {t('payment.months') || 'Ay'}</strong>
              </div>
              <div className="summary-row">
                <span>{t('payment.calculation') || 'Hesaplama'}:</span>
                <span>
                  {currency === 'USD'
                    ? `$${calculation.monthlyUsd.toFixed(2)} × ${months}`
                    : `₺${calculation.monthlyTry.toFixed(2)} × ${months}`}
                </span>
              </div>
              {currency === 'TRY' && (
                <div className="summary-row" style={{ fontSize: '0.8rem', color: '#9ca3af', fontStyle: 'italic' }}>
                  <span>{t('payment.exchangeRate') || 'Döviz Kuru'}:</span>
                  <span>1 USD = ₺{calculation.tryMultiplier}</span>
                </div>
              )}
              <div className="summary-row total">
                <span>{t('payment.total') || 'Toplam'}:</span>
                <strong>{calculation.currencySymbol}{calculation.amount.toFixed(2)}</strong>
              </div>
            </div>
          )}

          {/* BUTONLAR */}
          <div className="payment-actions">
            <button type="button" className="btn-secondary" onClick={onCancel} disabled={loading}>
              {t('action.cancel') || 'İptal'}
            </button>
            <button
              type="button"
              className="btn-primary"
              onClick={handleProceedToPayment}
              disabled={loading || !calculation}
            >
              {loading ? (t('action.processing') || 'İşleniyor...') : (t('action.next') || 'Devam Et →')}
            </button>
          </div>
        </div>
      )}

      {/* Ek stiller */}
      <style>{`
        .free-trial-section { margin-bottom: 4px; }
        .free-trial-card {
          background: linear-gradient(135deg, #ecfdf5 0%, #f0fdf4 100%);
          border: 2px solid #a7f3d0; border-radius: 12px; padding: 20px; text-align: center;
        }
        .free-trial-badge {
          display: inline-block; background: #059669; color: white;
          padding: 4px 12px; border-radius: 20px; font-size: 0.8rem; font-weight: 600; margin-bottom: 8px;
        }
        .free-trial-card h4 { margin: 8px 0 4px; font-size: 1.1rem; color: #065f46; }
        .free-trial-card p { margin: 0 0 12px; color: #6b7280; font-size: 0.85rem; }
        .btn-trial {
          padding: 10px 24px; background: #059669; color: white; border: none;
          border-radius: 8px; font-size: 0.95rem; font-weight: 600; cursor: pointer;
        }
        .btn-trial:hover { background: #047857; }
        .btn-trial:disabled { background: #9ca3af; cursor: not-allowed; }
        .or-divider {
          display: flex; align-items: center; margin: 20px 0; gap: 12px;
        }
        .or-divider::before, .or-divider::after {
          content: ''; flex: 1; height: 1px; background: #d1d5db;
        }
        .or-divider span { color: #9ca3af; font-size: 0.85rem; font-weight: 500; }
        .form-section { margin-bottom: 20px; }
        .section-label { display: block; font-weight: 600; color: #374151; margin-bottom: 8px; font-size: 0.9rem; }
        .currency-selector { display: flex; gap: 10px; }
        .currency-btn {
          flex: 1; display: flex; align-items: center; justify-content: center; gap: 8px;
          padding: 14px; border: 2px solid #e5e7eb; border-radius: 10px;
          background: white; cursor: pointer; font-size: 0.9rem; transition: all 0.15s;
        }
        .currency-btn:hover { border-color: #3b82f6; }
        .currency-btn.selected { border-color: #3b82f6; background: #eff6ff; color: #1d4ed8; font-weight: 600; }
        .months-select {
          width: 100%; padding: 12px 14px; border: 2px solid #e5e7eb;
          border-radius: 10px; font-size: 1rem; background: white; cursor: pointer;
        }
        .months-select:focus { border-color: #3b82f6; outline: none; }
        .quick-months { display: flex; gap: 8px; margin-top: 10px; }
        .quick-month-btn {
          padding: 6px 16px; border: 1px solid #d1d5db; border-radius: 20px;
          background: white; cursor: pointer; font-size: 0.8rem; transition: all 0.15s;
        }
        .quick-month-btn:hover { border-color: #3b82f6; color: #3b82f6; }
        .quick-month-btn.selected { background: #3b82f6; color: white; border-color: #3b82f6; }
        .btn-pay { background: #059669 !important; font-size: 1rem !important; }
        .btn-pay:hover { background: #047857 !important; }
        .form-section-title {
          font-weight: 600; font-size: 0.95rem; color: #374151;
          margin-bottom: 12px; padding-bottom: 8px; border-bottom: 1px solid #e5e7eb;
        }
        .form-section-hint {
          display: block; font-weight: 400; font-size: 0.75rem; color: #9ca3af;
          margin-top: 2px; font-style: italic;
        }
        .payment-step * {
            border-width: 1px;
            font-weight: 400 !important;
        }
      `}</style>
    </div>
  );
}

export default PaymentForm;
